import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Printer, Mail } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import LoadingSpinner from '../components/LoadingSpinner';
import { getDriverPaystub, emailPaystub } from '../api/paystubApi';
import { useToast } from '../context/ToastContext';

function getDefaultWeekRange() {
  const today = new Date();
  const day = today.getDay();
  const diffToMonday = day === 0 ? 6 : day - 1;
  const monday = new Date(today);
  monday.setDate(today.getDate() - diffToMonday);

  const format = (d) => d.toISOString().split('T')[0];
  return { from: format(monday), to: format(today) };
}

function Paystub() {
  const { id } = useParams();
  const { showToast } = useToast();

  const defaultRange = getDefaultWeekRange();
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const fetchPaystub = async (fromDate, toDate) => {
    setLoading(true);
    try {
      const result = await getDriverPaystub(id, fromDate, toDate);
      if (result.success) {
        setData(result);
      } else {
        showToast('error', result.message || 'Failed to load paystub.');
      }
    } catch (err) {
      showToast('error', 'Could not connect to server.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaystub(from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDateFilter = () => {
    fetchPaystub(from, to);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEmail = async () => {
    setSending(true);
    try {
      const result = await emailPaystub(id, from, to);
      if (result.success) {
        showToast('success', 'Paystub emailed to driver.');
      } else {
        showToast('error', result.message || 'Failed to send email.');
      }
    } catch (err) {
      showToast('error', 'Could not connect to server.');
    } finally {
      setSending(false);
    }
  };

  if (loading || !data) {
    return (
      <div style={{ display: 'flex' }}>
        <Sidebar />
        <div style={{ flex: 1 }}>
          <TopBar title="Loading..." />
          <LoadingSpinner fullScreen />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex' }}>
      <div className="no-print">
        <Sidebar />
      </div>

      <div style={{ flex: 1 }}>
        <div className="no-print">
          <TopBar
            title={`Paystub — ${data.driver.driver_name}`}
            rightAction={
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handlePrint} style={styles.printBtn}>
                  <Printer size={15} style={{ marginRight: 6 }} /> Print
                </button>
                <button onClick={handleEmail} disabled={sending} style={styles.emailBtn}>
                  <Mail size={15} style={{ marginRight: 6 }} /> {sending ? 'Sending...' : 'Email to Driver'}
                </button>
              </div>
            }
          />
        </div>

        <div style={{ padding: 24, maxWidth: 700 }}>
          <div className="no-print" style={styles.filterRow}>
            <div>
              <label style={styles.filterLabel}>From</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={styles.dateInput} />
            </div>
            <div>
              <label style={styles.filterLabel}>To</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={styles.dateInput} />
            </div>
            <button onClick={handleDateFilter} style={styles.applyBtn}>
              Apply
            </button>
          </div>

          <div style={styles.paystubSheet}>
            <div style={styles.paystubHeader}>
              <div>
                {/* Real logo, same asset used across the app (login screen, headers) */}
                <img src="/full-logo.png" alt="Hermann Trucking" style={styles.logo} />
                <div style={styles.paystubSubtitle}>Driver Paystub</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={styles.driverName}>{data.driver.driver_name}</div>
                <div style={styles.periodText}>{data.from} — {data.to}</div>
              </div>
            </div>

            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Load</th>
                  <th style={styles.th}>Route</th>
                  <th style={styles.th}>Date</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.payments.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={styles.emptyCell}>No payments in this period.</td>
                  </tr>
                ) : (
                  data.payments.map((p) => (
                    <tr key={p.id_payment}>
                      <td style={styles.td}>#{p.load_id}</td>
                      <td style={styles.td}>{p.origin} → {p.destination}</td>
                      <td style={styles.td}>{p.payment_date}</td>
                      <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>
                        {Number(p.amount_value).toLocaleString()} {p.currency}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <div style={styles.totalRow}>
              <span style={styles.totalLabel}>Total</span>
              <span style={styles.totalValue}>
                {Number(data.total).toLocaleString()} {data.currency}
              </span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  printBtn: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#eff6ff',
    color: '#2563eb',
    border: 'none',
    borderRadius: 8,
    padding: '9px 16px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  emailBtn: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    padding: '9px 16px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  filterRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 12,
    marginBottom: 20,
  },
  filterLabel: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#6b7280',
    marginBottom: 4,
  },
  dateInput: {
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    fontSize: 14,
  },
  applyBtn: {
    padding: '9px 18px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: '#111827',
    color: 'white',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
  },
  paystubSheet: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 32,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  paystubHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '2px solid #111827',
    paddingBottom: 16,
    marginBottom: 20,
  },
  logo: { height: 36, width: 'auto', display: 'block' },
  paystubSubtitle: { fontSize: 12, color: '#9ca3af', marginTop: 6 },
  driverName: { fontSize: 15, fontWeight: 700, color: '#111827' },
  periodText: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  table: { width: '100%', borderCollapse: 'collapse', marginBottom: 16 },
  th: {
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 700,
    color: '#9ca3af',
    textTransform: 'uppercase',
    padding: '8px 4px',
    borderBottom: '1px solid #e5e7eb',
  },
  td: {
    fontSize: 13,
    color: '#374151',
    padding: '10px 4px',
    borderBottom: '1px solid #f3f4f6',
  },
  emptyCell: {
    textAlign: 'center',
    color: '#9ca3af',
    fontSize: 13,
    padding: 20,
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    borderTop: '2px solid #111827',
    paddingTop: 14,
  },
  totalLabel: { fontSize: 16, fontWeight: 700, color: '#111827' },
  totalValue: { fontSize: 16, fontWeight: 700, color: '#2563eb' },
};

export default Paystub;